import type { PropsWithChildren, RefObject } from "react"
import {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react"
import { useQuery } from "@rocicorp/zero/react"

import { queries } from "@productlane/zero/queries"

import type { SuggestionProps } from "@tiptap/suggestion"
import { getPortalBaseUrl } from "@/lib/portal-base-url"
import { useZeroWorkspace } from "@/lib/zero"
import { buildHelpArticleSuggestionItems } from "./build-items"
import { HelpArticlesPopover } from "./help-articles-popover"

export interface HelpArticleSuggestionActions {
	openHelpArticles: () => void
}

export interface HelpArticlesProps {
	withoutPortal?: boolean
	width?: number
	initialOpen?: boolean
	anchorRef?: RefObject<HTMLElement | null>
	caretRef?: RefObject<{ top: number; left: number } | null>
}

const HelpArticleSuggestions = forwardRef<
	HelpArticleSuggestionActions,
	PropsWithChildren<
		Pick<SuggestionProps, "editor"> &
			Partial<Omit<SuggestionProps, "editor">> &
			HelpArticlesProps
	>
>(
	(
		{
			children,
			command,
			editor,
			width,
			withoutPortal = false,
			initialOpen,
			anchorRef,
			caretRef,
		},
		ref,
	) => {
		const workspace = useZeroWorkspace()
		const [articles] = useQuery(queries.helpCenterArticles())
		const popoverRef = useRef<{ setOpen: (open: boolean) => void }>(null)

		// Agent and internal articles are excluded: this inserts a link to the docs
		// site, which would 404 for anyone who follows it.
		const publicArticles = useMemo(
			() =>
				articles.filter(
					(a) =>
						a.published &&
						a.visibility === "PUBLIC" &&
						!a.archived &&
						!a.isDeleted,
				),
			[articles],
		)

		const portalUrl = useMemo(
			() =>
				workspace
					? getPortalBaseUrl({
							domain: workspace.domain,
							customDomain: workspace.customDomain ?? null,
						})
					: "",
			[workspace],
		)

		const articleItems = useMemo(
			() => buildHelpArticleSuggestionItems(publicArticles, portalUrl),
			[publicArticles, portalUrl],
		)

		const handleCommand = useCallback(
			(index: number) => {
				const item = articleItems[index]
				if (item) {
					command?.(item)
				}
			},
			[command, articleItems],
		)

		useImperativeHandle(ref, () => ({
			openHelpArticles: () => {
				popoverRef.current?.setOpen(true)
			},
		}))

		if (typeof document === "undefined") {
			return null
		}

		return (
			<HelpArticlesPopover
				ref={popoverRef}
				withoutPortal={withoutPortal}
				width={width}
				items={articleItems}
				onSelect={handleCommand}
				editor={editor}
				initialOpen={initialOpen}
				anchorRef={anchorRef}
				caretRef={caretRef}
			>
				{children}
			</HelpArticlesPopover>
		)
	},
)

export default HelpArticleSuggestions
